/**
 * Client-side media inspection for outgoing attachments.
 *
 * The SDK stores whatever metadata we hand it and never opens the file, so
 * anything a recipient's bubble needs — dimensions to reserve layout space, a
 * duration for the audio scrubber, a poster frame for a video — has to be
 * measured here, on the sender's machine, before the send.
 *
 * Everything degrades quietly: a probe that fails returns nulls and the bubble
 * falls back to a plain icon + filename. A missing poster is a cosmetic loss,
 * never a failed send.
 */

import type { AttachmentKind } from './messenger.wire'

export interface ProbedMedia {
  kind: AttachmentKind
  width: number | null
  height: number | null
  durationMs: number | null
  /**
   * A small preview image to upload alongside the attachment, surfaced as
   * `thumbnailUrl`. Video poster frame, downscaled photo, or a PDF's first
   * page — whichever applies. Null when we can't produce one.
   *
   * This is what lets a recipient see WHAT arrived before deciding to download
   * it (blurred behind the auto-download placeholder), and what makes a
   * document bubble show its contents instead of a generic icon.
   */
  poster: File | null
}

/** Longest edge of a generated preview. Small enough to be near-free to fetch,
 *  large enough to stay legible when a document preview is tapped open. */
const PREVIEW_MAX_EDGE = 480

/** Map a mime to a render kind. `voice` is never inferred — a recorded note and
 *  an uploaded audio file share `audio/*`, so only the caller knows. */
export function kindForMime(mime: string): AttachmentKind {
  if (mime.startsWith('image/')) return 'photo'
  if (mime.startsWith('video/')) return 'video'
  if (mime.startsWith('audio/')) return 'audio'
  return 'document'
}

/** Read intrinsic dimensions / duration, and grab a poster frame for video. */
export async function probeMedia(file: File, kind: AttachmentKind): Promise<ProbedMedia> {
  const empty: ProbedMedia = { kind, width: null, height: null, durationMs: null, poster: null }
  try {
    if (kind === 'photo') {
      const { width, height } = await imageSize(file)
      return { ...empty, width, height, poster: await downscaleImage(file) }
    }
    if (kind === 'document' && file.type === 'application/pdf') {
      return { ...empty, poster: await pdfFirstPage(file) }
    }
    if (kind === 'video') {
      return await videoMeta(file)
    }
    if (kind === 'audio' || kind === 'voice') {
      const durationMs = await mediaDuration(file, 'audio')
      return { ...empty, durationMs }
    }
  } catch {
    // Probing is best-effort — never block a send on it.
  }
  return empty
}

function imageSize(file: File): Promise<{ width: number | null; height: number | null }> {
  return withObjectUrl(file, (url) =>
    new Promise((resolve) => {
      const img = new Image()
      img.onload = () => resolve({ width: img.naturalWidth, height: img.naturalHeight })
      img.onerror = () => resolve({ width: null, height: null })
      img.src = url
    }),
  )
}

function videoMeta(file: File): Promise<ProbedMedia> {
  return withObjectUrl(file, (url) =>
    new Promise((resolve) => {
      const video = document.createElement('video')
      video.preload = 'metadata'
      video.muted = true
      video.playsInline = true

      const fail = () =>
        resolve({ kind: 'video', width: null, height: null, durationMs: null, poster: null })

      video.onerror = fail
      video.onloadedmetadata = () => {
        const width = video.videoWidth || null
        const height = video.videoHeight || null
        const durationMs = Number.isFinite(video.duration) ? Math.round(video.duration * 1000) : null

        // Seek a little way in — frame 0 of a lot of videos is a black frame.
        const target = Math.min(0.1, (video.duration || 0) / 2)
        video.onseeked = () => {
          resolve({ kind: 'video', width, height, durationMs, poster: grabFrame(video) })
        }
        video.onerror = () => resolve({ kind: 'video', width, height, durationMs, poster: null })
        try {
          video.currentTime = target
        } catch {
          resolve({ kind: 'video', width, height, durationMs, poster: null })
        }
      }
      video.src = url
    }),
  )
}

function grabFrame(video: HTMLVideoElement): File | null {
  try {
    const canvas = document.createElement('canvas')
    canvas.width = video.videoWidth
    canvas.height = video.videoHeight
    const ctx = canvas.getContext('2d')
    if (!ctx || !canvas.width || !canvas.height) return null
    ctx.drawImage(video, 0, 0, canvas.width, canvas.height)
    const dataUrl = canvas.toDataURL('image/jpeg', 0.7)
    return dataUrlToFile(dataUrl, 'poster.jpg', 'image/jpeg')
  } catch {
    // Tainted canvas (cross-origin source) or an unsupported codec.
    return null
  }
}

function mediaDuration(file: File, tag: 'audio' | 'video'): Promise<number | null> {
  return withObjectUrl(file, (url) =>
    new Promise((resolve) => {
      const el = document.createElement(tag)
      el.preload = 'metadata'
      el.onloadedmetadata = () =>
        resolve(Number.isFinite(el.duration) ? Math.round(el.duration * 1000) : null)
      el.onerror = () => resolve(null)
      el.src = url
    }),
  )
}

/** Run `fn` against an object URL and always revoke it — these leak otherwise. */
async function withObjectUrl<T>(file: File, fn: (url: string) => Promise<T>): Promise<T> {
  const url = URL.createObjectURL(file)
  try {
    return await fn(url)
  } finally {
    URL.revokeObjectURL(url)
  }
}

export function dataUrlToFile(dataUrl: string, name: string, mime: string): File | null {
  try {
    const [, base64] = dataUrl.split(',')
    if (!base64) return null
    const bytes = atob(base64)
    const buf = new Uint8Array(bytes.length)
    for (let i = 0; i < bytes.length; i += 1) buf[i] = bytes.charCodeAt(i)
    return new File([buf], name, { type: mime })
  } catch {
    return null
  }
}

/** "39 KB" — the size badge on a preview bubble. */
export function formatBytes(bytes: number | null | undefined): string {
  if (!bytes || bytes <= 0) return ''
  const units = ['B', 'KB', 'MB', 'GB']
  let value = bytes
  let unit = 0
  while (value >= 1024 && unit < units.length - 1) {
    value /= 1024
    unit += 1
  }
  return `${value < 10 && unit > 0 ? value.toFixed(1) : Math.round(value)} ${units[unit]}`
}

/** "1:07" — running time on an audio/video bubble. */
export function formatDuration(ms: number | null | undefined): string {
  if (!ms || ms <= 0) return ''
  const total = Math.round(ms / 1000)
  const mins = Math.floor(total / 60)
  const secs = total % 60
  return `${mins}:${String(secs).padStart(2, '0')}`
}


/** Downscale an image to a small JPEG preview. Returns null if it's already
 *  tiny (no point paying for a second object) or the decode fails. */
async function downscaleImage(file: File): Promise<File | null> {
  try {
    const bitmap = await createImageBitmap(file)
    const longest = Math.max(bitmap.width, bitmap.height)
    if (longest <= PREVIEW_MAX_EDGE) {
      bitmap.close()
      return null
    }
    const scale = PREVIEW_MAX_EDGE / longest
    const canvas = document.createElement('canvas')
    canvas.width = Math.round(bitmap.width * scale)
    canvas.height = Math.round(bitmap.height * scale)
    const ctx = canvas.getContext('2d')
    if (!ctx) { bitmap.close(); return null }
    ctx.drawImage(bitmap, 0, 0, canvas.width, canvas.height)
    bitmap.close()
    return dataUrlToFile(canvas.toDataURL('image/jpeg', 0.6), 'preview.jpg', 'image/jpeg')
  } catch {
    return null
  }
}

/**
 * Render a PDF's first page to a JPEG.
 *
 * `pdfjs-dist` is imported dynamically so it never enters the main bundle —
 * it only loads when someone actually attaches a PDF.
 */
async function pdfFirstPage(file: File): Promise<File | null> {
  try {
    const pdfjs = await import('pdfjs-dist')
    // The worker ships with the package; point at it via a module URL so the
    // bundler emits it as its own chunk.
    pdfjs.GlobalWorkerOptions.workerSrc = new URL(
      'pdfjs-dist/build/pdf.worker.min.mjs',
      import.meta.url,
    ).toString()

    const doc = await pdfjs.getDocument({ data: await file.arrayBuffer() }).promise
    const page = await doc.getPage(1)
    const base = page.getViewport({ scale: 1 })
    const viewport = page.getViewport({
      scale: PREVIEW_MAX_EDGE / Math.max(base.width, base.height),
    })

    const canvas = document.createElement('canvas')
    canvas.width = Math.round(viewport.width)
    canvas.height = Math.round(viewport.height)
    const ctx = canvas.getContext('2d')
    if (!ctx) return null
    // A PDF page is transparent where unpainted; without this the preview is
    // black on dark backgrounds.
    ctx.fillStyle = '#ffffff'
    ctx.fillRect(0, 0, canvas.width, canvas.height)
    await page.render({ canvasContext: ctx, viewport }).promise
    void doc.destroy()
    return dataUrlToFile(canvas.toDataURL('image/jpeg', 0.7), 'preview.jpg', 'image/jpeg')
  } catch {
    // No preview is a cosmetic loss — never fail the send over it.
    return null
  }
}
